// Settings › Campaigns, beside the tracking numbers (BL-001, AdTrack's forwarding): what happens to a
// call nobody answers (the main line, how long each number rings, whether answered calls are recorded)
// and the numbers turned away. They matter only once a number's Voice URL at Twilio points at
// Flowology (docs/specs/call-forwarding-cutover.md); until then they change nothing. In Blueprint each
// is a part under the tracking numbers, in Riverstone its own card.

import { useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { normalizePhone } from '@flowology/kernel';
import { client } from '../api.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { phoneText } from '../types.ts';
import { blockedCount, unansweredMeans, type BlockedNumber, type Unanswered } from './campaigns-model.ts';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');
const PAGE = 50;

/** A part in Blueprint (a hairline and a small heading), a card in Riverstone. */
function Part({ bp, label, children }: { bp: boolean; label: string; children: ReactNode }) {
  return bp ? (
    <div className="bp-part" role="group" aria-label={label}>
      <h3>{label}</h3>
      {children}
    </div>
  ) : (
    <section className="stone card" aria-label={label}>
      <h2 className="h3">{label}</h2>
      {children}
    </section>
  );
}

type UnansweredDraft = { mainLine: string; ringSeconds: string; record: boolean };

export function UnansweredCalls({ canEdit, bp }: { canEdit: boolean; bp: boolean }) {
  const [now, setNow] = useState<Unanswered | null>(null);
  const [draft, setDraft] = useState<UnansweredDraft | null>(null);
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    const get = <T,>(key: string) => client.call<{ value: T }>('settings.get', { key }).then((r) => r.value);
    void Promise.all([
      get<string | null>('calls.mainLine'),
      get<number>('calls.ringSeconds'),
      get<boolean>('calls.recordForwarded'),
    ])
      .then(([mainLine, ringSeconds, record]) => setNow({ mainLine, ringSeconds, record }))
      .catch((e: unknown) => setError(say(e)));
  }, [again]);

  const save = async (d: UnansweredDraft) => {
    const main = d.mainLine.trim() ? normalizePhone(d.mainLine) : null;
    if (d.mainLine.trim() && !main) {
      setError(`“${d.mainLine.trim()}” isn’t a phone number.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (main) await client.call('settings.set', { key: 'calls.mainLine', value: main });
      else await client.call('settings.clear', { key: 'calls.mainLine' });
      await client.call('settings.set', { key: 'calls.ringSeconds', value: Number(d.ringSeconds) });
      await client.call('settings.set', { key: 'calls.recordForwarded', value: d.record });
      setSaid('Saved. It applies from the next call.');
      setDraft(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Part bp={bp} label="When nobody answers">
      {!now && !error && <p className="muted">Getting the settings…</p>}
      {now && !draft && (
        <ul className="campaign-list">
          <li>
            <div>
              <b>{now.mainLine ? `Main line ${phoneText(now.mainLine)}` : 'No main line'}</b>
              <span className="muted small block">{unansweredMeans(now, phoneText)}</span>
            </div>
            {canEdit && (
              <button
                type="button"
                className="btn soft"
                aria-label="Change what happens when nobody answers"
                onClick={() => {
                  setSaid(null);
                  setDraft({
                    mainLine: now.mainLine ? phoneText(now.mainLine) : '',
                    ringSeconds: String(now.ringSeconds),
                    record: now.record,
                  });
                }}
              >
                Change
              </button>
            )}
          </li>
        </ul>
      )}
      {draft && (
        <form
          className="fgrid campaign-form"
          aria-label="When nobody answers"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void save(draft);
          }}
        >
          <label>
            <span className="flabel">Main line</span>
            <input
              className="field"
              type="tel"
              value={draft.mainLine}
              maxLength={40}
              placeholder="The office’s number"
              onChange={(e) => setDraft({ ...draft, mainLine: e.target.value })}
            />
          </label>
          <label>
            <span className="flabel">Each number rings for</span>
            <select
              className="field"
              value={draft.ringSeconds}
              onChange={(e) => setDraft({ ...draft, ringSeconds: e.target.value })}
            >
              {[...new Set([10, 15, 20, 25, 30, 40, 50, 60, Number(draft.ringSeconds)])]
                .sort((a, b) => a - b)
                .map((s) => (
                  <option key={s} value={String(s)}>
                    {s} seconds
                  </option>
                ))}
            </select>
          </label>
          <label className="check wide">
            <input
              type="checkbox"
              checked={draft.record}
              onChange={(e) => setDraft({ ...draft, record: e.target.checked })}
            />
            Record answered calls, both sides (tell callers they’re recorded)
          </label>
          <div className="row wide">
            <SaveButton
              className="btn primary"
              busy={busy}
              busyLabel="Saving…"
              onClick={() => void save(draft)}
            >
              Save
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Part>
  );
}

type BlockDraft = { phone: string; note: string };

export function BlockedNumbers({ canEdit, bp }: { canEdit: boolean; bp: boolean }) {
  const [numbers, setNumbers] = useState<BlockedNumber[] | null>(null);
  const [total, setTotal] = useState(0);
  const [next, setNext] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<BlockDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  type Page = { numbers: BlockedNumber[]; total: number; next: string | null };
  useEffect(() => {
    void client
      .call<Page>('blockedNumbers.list', { limit: PAGE })
      .then((r) => {
        setNumbers(r.numbers);
        setTotal(r.total);
        setNext(r.next);
      })
      .catch((e: unknown) => setError(say(e)));
  }, [again]);

  const more = async () => {
    if (!next) return;
    setBusy(true);
    try {
      const r = await client.call<Page>('blockedNumbers.list', { limit: PAGE, after: next });
      setNumbers((was) => [...(was ?? []), ...r.numbers]);
      setTotal(r.total);
      setNext(r.next);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const write = async (run: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      setSaid(await run());
      setDraft(null);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const block = (d: BlockDraft) =>
    write(async () => {
      const r = await client.call<BlockedNumber>('blockedNumbers.add', {
        phone: d.phone.trim(),
        note: d.note.trim() || null,
      });
      return `${r.text} blocked: its calls hear busy from the next one.`;
    });
  const unblock = (b: BlockedNumber) =>
    write(async () => {
      await client.call('blockedNumbers.remove', { phone: b.phone });
      return `${b.text} unblocked.`;
    });

  return (
    <Part bp={bp} label="Blocked numbers">
      <p className="muted">
        Spam and robocalls: a call from one hears busy and isn’t kept. Never a customer’s number.
        {numbers && <> {blockedCount(total)}</>}
      </p>
      {!numbers && !error && <p className="muted">Getting the blocked numbers…</p>}
      {draft && (
        <form
          className="fgrid campaign-form"
          aria-label="Block a number"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.phone.trim() && !busy) void block(draft);
          }}
        >
          <label>
            <span className="flabel">Number</span>
            <input
              className="field"
              type="tel"
              value={draft.phone}
              maxLength={40}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
          </label>
          <label>
            <span className="flabel">Why (optional)</span>
            <input
              className="field"
              value={draft.note}
              maxLength={200}
              placeholder="Robocall"
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            />
          </label>
          <div className="row wide">
            <SaveButton
              className="btn primary"
              busy={busy}
              busyLabel="Blocking…"
              disabled={!draft.phone.trim()}
              onClick={() => void block(draft)}
            >
              Block
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {numbers && numbers.length > 0 && (
        <ul className="campaign-list" aria-label="Blocked">
          {numbers.map((b) => (
            <li key={b.phone}>
              <div>
                <b>{b.text}</b>
                {b.note && <span className="muted small block">{b.note}</span>}
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="btn quiet"
                  aria-label={`Unblock ${b.text}`}
                  disabled={busy}
                  onClick={() => void unblock(b)}
                >
                  Unblock
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {next && (
        <button
          type="button"
          className={bp ? 'bp-more' : 'btn quiet'}
          disabled={busy}
          onClick={() => void more()}
        >
          Show more ({total - (numbers?.length ?? 0)} more)
        </button>
      )}
      {canEdit && !draft && (
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSaid(null);
              setDraft({ phone: '', note: '' });
            }}
          >
            Block a number
          </button>
        </div>
      )}
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Part>
  );
}
